import { UserRole } from '@/types/user';
import { APP_ROUTES } from '@/ui/application-routes';
import { TFunction } from 'i18next';
import {
  BarChart3,
  Building2,
  FileText,
  Handshake,
  LayoutDashboard,
  LucideIcon,
  Plus,
  Settings,
  Users2,
} from 'lucide-react';

export type DashNavigationItem = {
  to: string;
  icon: LucideIcon;
  label: string;
  activeRoutes?: string[];
};

export type DashNavigationSection = {
  label?: string;
  separator?: boolean;
  items: DashNavigationItem[];
};

type GetDashNavigationSectionsParams = {
  role?: UserRole;
  isPerformancePartner?: boolean;
  t: TFunction;
};

const accountSettingsItem = (t: TFunction): DashNavigationItem => ({
  to: APP_ROUTES.SETTINGS.MAIN,
  icon: Settings,
  label: t('nav.settings'),
  activeRoutes: [
    APP_ROUTES.SETTINGS.PROFILE,
    APP_ROUTES.SETTINGS.COACHING,
    APP_ROUTES.SETTINGS.KNOWLEDGE_BASE,
    APP_ROUTES.SETTINGS.LANGUAGE,
    APP_ROUTES.SETTINGS.COMPANY_CONTROLS,
    APP_ROUTES.SETTINGS.INTEGRATIONS,
    APP_ROUTES.SETTINGS.BILLING,
    APP_ROUTES.SETTINGS.SECURITY,
    APP_ROUTES.SETTINGS.COMMANDER,
    APP_ROUTES.COMPANY.USERS,
    APP_ROUTES.COMPANY.TEAMS,
    APP_ROUTES.COMPANY.TEAM_DETAIL,
  ],
});

const userWorkspaceItems = (t: TFunction): DashNavigationItem[] => [
  {
    to: APP_ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    label: t('nav.me'),
    activeRoutes: [APP_ROUTES.DASHBOARD_V2, APP_ROUTES.DASHBOARD_CLASSIC],
  },
  {
    to: APP_ROUTES.REPORTS.MAIN,
    icon: FileText,
    label: t('nav.reports'),
  },
];

const performancePartnerItem = (t: TFunction): DashNavigationItem => ({
  to: APP_ROUTES.COACH.MAIN,
  icon: Handshake,
  label: t('nav.performancePartner'),
  activeRoutes: [
    APP_ROUTES.COACH.MEMBER,
    APP_ROUTES.COACH.COMPANY,
    APP_ROUTES.COACH.TEAM,
  ],
});

export const getDashNavigationSections = ({
  role,
  isPerformancePartner = false,
  t,
}: GetDashNavigationSectionsParams): DashNavigationSection[] => {
  const sections = getRoleNavigationSections(role, t);
  if (!isPerformancePartner) return sections;
  // PP is an add-on: it joins the user's normal workspace instead of replacing it.
  const [first, ...rest] = sections;
  return [
    { ...first, items: [...first.items, performancePartnerItem(t)] },
    ...rest,
  ];
};

const getRoleNavigationSections = (
  role: UserRole | undefined,
  t: TFunction
): DashNavigationSection[] => {
  if (role === UserRole.CHANNEL_PARTNER_ADMIN) {
    return [
      {
        label: t('nav.sectionCommandCenter'),
        items: [
          {
            to: APP_ROUTES.PARTNER.DASHBOARD,
            icon: Building2,
            label: t('nav.partnerDashboard'),
          },
          {
            to: APP_ROUTES.ANALYTICS.PARTNER,
            icon: BarChart3,
            label: t('nav.analytics'),
          },
          ...userWorkspaceItems(t),
        ],
      },
      {
        label: t('nav.sectionAdmin'),
        items: [
          {
            to: APP_ROUTES.PARTNER.ONBOARD_COMPANY,
            icon: Plus,
            label: t('nav.onboardCompany'),
          },
        ],
      },
      {
        separator: true,
        items: [accountSettingsItem(t)],
      },
    ];
  }

  if (role === UserRole.COMPANY_ADMIN) {
    return [
      {
        label: t('nav.sectionCommandCenter'),
        items: [
          ...userWorkspaceItems(t),
          {
            to: APP_ROUTES.ANALYTICS.COMPANY,
            icon: BarChart3,
            label: t('nav.company'),
          },
          {
            to: APP_ROUTES.TEAM.DASHBOARD,
            icon: Users2,
            label: t('nav.teams'),
            activeRoutes: [APP_ROUTES.TEAM.DETAIL, APP_ROUTES.ANALYTICS.TEAM],
          },
        ],
      },
      {
        separator: true,
        items: [accountSettingsItem(t)],
      },
    ];
  }

  if (role === UserRole.COMPANY_MANAGER) {
    return [
      {
        label: t('nav.sectionCommandCenter'),
        items: [
          ...userWorkspaceItems(t),
          {
            to: APP_ROUTES.TEAM.DASHBOARD,
            icon: Users2,
            label: t('nav.teams'),
            activeRoutes: [APP_ROUTES.TEAM.DETAIL, APP_ROUTES.ANALYTICS.TEAM],
          },
        ],
      },
      {
        separator: true,
        items: [accountSettingsItem(t)],
      },
    ];
  }

  return [
    {
      label: t('nav.sectionCommandCenter'),
      items: userWorkspaceItems(t),
    },
    {
      separator: true,
      items: [accountSettingsItem(t)],
    },
  ];
};
